import { type VersionComparison } from '@consigli/types';

export interface ContentSubGroup {
  key: string;
  content: string | null;
  refContent: string | null;
  blobName: string | null;
  pageNumber: number | null;
  refBlobName: string | null;
  refPageNumber: number | null;
  findings: VersionComparison[];
}

export interface FindingGroup {
  key: string;
  blobName: string | null;
  pageNumber: number | null;
  matchConfidence: number | null;
  findings: VersionComparison[];
  subGroups: ContentSubGroup[];
}

const buildGroupKey = (
  blobId: string | null | undefined,
  pageNumber: number | null | undefined,
): string => `${blobId ?? '_none_'}::${pageNumber ?? '_none_'}`;

// Unit separator keeps the two extracts from colliding (e.g. "a" + "" vs "" + "a").
const buildContentKey = (
  content: string | null | undefined,
  refContent: string | null | undefined,
): string => `${(content ?? '').trim()}\u241F${(refContent ?? '').trim()}`;

// Group findings within a single page that share the same Version A / Version B
// extract, preserving first-appearance order.
const buildContentSubGroups = (
  pageKey: string,
  findings: VersionComparison[],
): ContentSubGroup[] => {
  const subGroupMap = new Map<string, ContentSubGroup>();

  for (const item of findings) {
    const contentKey = buildContentKey(item.content, item.refContent);
    const existing = subGroupMap.get(contentKey);
    if (existing) {
      existing.findings.push(item);
    } else {
      subGroupMap.set(contentKey, {
        key: `${pageKey}::${contentKey}`,
        content: item.content ?? null,
        refContent: item.refContent ?? null,
        blobName: item.blobName ?? null,
        pageNumber: item.pageNumber ?? null,
        refBlobName: item.refBlobName ?? null,
        refPageNumber: item.refPageNumber ?? null,
        findings: [item],
      });
    }
  }

  return [...subGroupMap.values()];
};

const compareNullableString = (a: string | null, b: string | null): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a.localeCompare(b);
};

const compareNullableNumber = (a: number | null, b: number | null): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
};

export const groupComparisonsByPage = (items: VersionComparison[]): FindingGroup[] => {
  const groupMap = new Map<string, FindingGroup>();

  for (const item of items) {
    const hasPrimaryBlob = item.blobId != null;
    const blobId = hasPrimaryBlob ? item.blobId : item.refBlobId;
    const blobName = hasPrimaryBlob ? (item.blobName ?? null) : (item.refBlobName ?? null);
    const pageNumber = hasPrimaryBlob ? (item.pageNumber ?? null) : (item.refPageNumber ?? null);

    const key = buildGroupKey(blobId, pageNumber);

    const existing = groupMap.get(key);
    if (existing) {
      existing.findings.push(item);
    } else {
      groupMap.set(key, {
        key,
        blobName,
        pageNumber,
        matchConfidence: item.matchConfidence ?? null,
        findings: [item],
        subGroups: [],
      });
    }
  }

  return [...groupMap.values()]
    .map((group) => {
      const subGroups = buildContentSubGroups(group.key, group.findings);
      return {
        ...group,
        subGroups,
        // Reorder the flat list to match sub-group display order so PDF-panel
        // next/previous navigation follows what is on screen.
        findings: subGroups.flatMap((subGroup) => subGroup.findings),
      };
    })
    .sort((a, b) => {
      const nameCompare = compareNullableString(a.blobName, b.blobName);
      if (nameCompare !== 0) return nameCompare;
      return compareNullableNumber(a.pageNumber, b.pageNumber);
    });
};
